import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PublicDiagnosticState } from './types';
import { PamNote } from './components/PamNote';
import { CheckInbox, KeepItPanel, type KeepItSignIn } from './KeepIt';

export interface PublicDiagnosticEndScreenProps {
  state: PublicDiagnosticState;
  signedIn?: boolean;
  onContinue?: () => void;
  onStartOver?: () => void;
  /** Signed out: the keep-it step replaces Keep going. */
  keepIt?: KeepItSignIn;
  /** Signed in with the free week already running (trial at mint). */
  trialStarted?: boolean;
}

const OWNERSHIP: {
  flag: keyof Pick<
    PublicDiagnosticState,
    'simulationBegun' | 'cardScheduled' | 'pamAnswerReceived'
  >;
  line: string;
}[] = [
  { flag: 'simulationBegun', line: 'You started a clinical simulation.' },
  { flag: 'cardScheduled', line: 'A flashcard is scheduled to return.' },
  { flag: 'pamAnswerReceived', line: 'You have an answer from Pam.' },
];

export const PublicDiagnosticEndScreen: React.FC<PublicDiagnosticEndScreenProps> = ({
  state,
  signedIn = false,
  onContinue,
  onStartOver,
  keepIt,
  trialStarted = false,
}) => {
  const owned = OWNERSHIP.filter(({ flag }) => state[flag]);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const showKeepIt = keepIt != null && !signedIn;

  if (showKeepIt && sentTo) {
    return (
      <div className="max-w-xl mx-auto" data-testid="public-diagnostic-end-screen">
        <CheckInbox email={sentTo} onDifferentEmail={() => setSentTo(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-xl mx-auto" data-testid="public-diagnostic-end-screen">
      <div className="card space-y-4">
        <p className="eyebrow text-primary-700">Six of six done</p>
        <h2 className="heading-2">This is yours now</h2>
        <p className="body-base">
          You built a starting point you can keep. Nothing here is a score to chase.
        </p>

        {owned.length > 0 && (
          <ul className="space-y-2">
            {owned.map(({ flag, line }) => (
              <li
                key={flag}
                className="body-base text-neutral-800 font-semibold"
                data-testid={`ownership-${flag}`}
              >
                <span aria-hidden="true">✓ </span>
                {line}
              </li>
            ))}
          </ul>
        )}

        {showKeepIt ? (
          <KeepItPanel signIn={keepIt} onSent={setSentTo} />
        ) : signedIn && trialStarted ? (
          <Link to="/dashboard" className="btn-primary w-full" data-testid="public-diagnostic-trial-started">
            Your free week has started. Go to your dashboard
          </Link>
        ) : onContinue && (
          <div className="space-y-2 pt-1">
            <button
              type="button"
              onClick={onContinue}
              className="btn-primary w-full"
              data-testid="public-diagnostic-keep-going"
            >
              Keep going
            </button>
            <p className="caption text-center">
              {signedIn
                ? 'Takes you back to your account.'
                : 'Save it to a free account. 7 days of everything, no card.'}
            </p>
          </div>
        )}

        {onStartOver && (
          <button
            type="button"
            onClick={onStartOver}
            className="w-full py-2 text-sm font-semibold text-primary-700 underline-offset-2 hover:underline"
            data-testid="public-diagnostic-start-over"
          >
            Start over
          </button>
        )}
      </div>

      <PamNote label="Pam's tip">
        Save this progress and keep going when you are ready. You already own the start.
      </PamNote>
    </div>
  );
};
